import React, { useMemo, useState, createElement } from 'react';
import jsPDF from 'jspdf';
import { useData } from '../../store/DataContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { DownloadIcon, FileSpreadsheetIcon, PrinterIcon } from 'lucide-react';
import { expiryAlertWindow, formatCurrency, isLowStockOnly, isOutOfStock } from './pharmacyUtils';
type ReportType = 'current-inventory' | 'low-stock' | 'out-of-stock' | 'near-expiry' | 'expired' | 'daily-dispensing' | 'monthly-dispensing' | 'purchase-orders' | 'supplier-purchases' | 'batch-tracking' | 'inventory-valuation' | 'fast-moving' | 'slow-moving' | 'adjustment-history' | 'medicine-returns' | 'movement-audit';
type ReportRow = Record<string, string | number>;
const reports: Array<{
  value: ReportType;
  label: string;
}> = [{
  value: 'current-inventory',
  label: 'Current Inventory'
}, {
  value: 'low-stock',
  label: 'Low Stock'
}, {
  value: 'out-of-stock',
  label: 'Out of Stock'
}, {
  value: 'near-expiry',
  label: 'Near Expiry'
}, {
  value: 'expired',
  label: 'Expired Medicines'
}, {
  value: 'daily-dispensing',
  label: 'Daily Dispensing'
}, {
  value: 'monthly-dispensing',
  label: 'Monthly Dispensing'
}, {
  value: 'purchase-orders',
  label: 'Purchase Orders'
}, {
  value: 'supplier-purchases',
  label: 'Supplier Purchases'
}, {
  value: 'batch-tracking',
  label: 'Batch Tracking'
}, {
  value: 'inventory-valuation',
  label: 'Inventory Valuation'
}, {
  value: 'fast-moving',
  label: 'Fast Moving Medicines'
}, {
  value: 'slow-moving',
  label: 'Slow Moving Medicines'
}, {
  value: 'adjustment-history',
  label: 'Stock Adjustment History'
}, {
  value: 'medicine-returns',
  label: 'Medicine Returns'
}, {
  value: 'movement-audit',
  label: 'Stock Movement Audit'
}];
export function InventoryReports() {
  const {
    medicines,
    medicineBatches,
    suppliers,
    purchaseOrders,
    stockMovements,
    users
  } = useData();
  const [reportType, setReportType] = useState<ReportType>('current-inventory');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [category, setCategory] = useState('');
  const [medicineId, setMedicineId] = useState('');
  const [pharmacistId, setPharmacistId] = useState('');
  const categories = useMemo(() => [...new Set(medicines.map(medicine => medicine.category))].sort(), [medicines]);
  const rows = useMemo(() => buildRows({
    reportType,
    medicines,
    medicineBatches,
    purchaseOrders,
    stockMovements,
    supplierId,
    category,
    medicineId,
    pharmacistId,
    from,
    to
  }), [reportType, medicines, medicineBatches, purchaseOrders, stockMovements, supplierId, category, medicineId, pharmacistId, from, to]);
  const columns = rows.length ? Object.keys(rows[0]) : ['Status'];
  const reportLabel = reports.find(report => report.value === reportType)?.label || 'Pharmacy Report';
  const exportCsv = () => {
    const csv = [columns.join(','), ...rows.map(row => columns.map(column => csvValue(row[column])).join(','))].join('\n');
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;'
    });
    downloadBlob(blob, `${slug(reportLabel)}-${dateStamp()}.csv`);
  };
  const exportPdf = () => {
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'pt',
      format: 'a4'
    });
    const margin = 32;
    let y = margin;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.text(reportLabel, margin, y);
    y += 18;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.text(`Generated ${new Date().toLocaleString()} · ${rows.length} records`, margin, y);
    y += 18;
    const width = 770 / columns.length;
    const header = () => {
      pdf.setFont('helvetica', 'bold');
      columns.forEach((column, index) => pdf.text(column, margin + index * width, y));
      y += 5;
      pdf.line(margin, y, margin + 770, y);
      y += 11;
      pdf.setFont('helvetica', 'normal');
    };
    header();
    rows.forEach(row => {
      if (y > 550) {
        pdf.addPage();
        y = margin;
        header();
      }
      columns.forEach((column, index) => {
        const text = String(row[column] ?? '—');
        pdf.text(pdf.splitTextToSize(text, width - 6).slice(0, 2), margin + index * width, y);
      });
      y += 20;
    });
    pdf.save(`${slug(reportLabel)}-${dateStamp()}.pdf`);
  };
  return <div className="space-y-5" data-id="element-4972" data-mp="245fg0c77p7js">
      <div className="flex flex-col gap-3 border-b border-slate-200 pb-5 sm:flex-row sm:items-end sm:justify-between print:hidden" data-id="element-4973" data-mp="2eb6wiwiubgah">
        <div data-id="element-4974" data-mp="29e6ser5jwnm0">
          <h2 className="text-xl font-bold text-slate-950" data-id="element-4975" data-mp="29eghmciruqqn">
            Inventory Reports
          </h2>
          <p className="mt-1 text-sm text-slate-500" data-id="element-4976" data-mp="2n64x8ps8bxcd">
            Live reports generated directly from the pharmacy ledger, batch
            register, and procurement data.
          </p>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-4977" data-mp="2x4avmxetulch">
          <Button variant="outline" onClick={() => window.print()} data-id="element-4978" data-mp="2s3wkh6hxiva8">
            <PrinterIcon className="mr-2 h-4 w-4" data-id="element-4979" data-mp="362w94e5fza6e" /> Print
          </Button>
          <Button variant="outline" onClick={exportPdf} data-id="element-4980" data-mp="360s8q66r4tav">
            <DownloadIcon className="mr-2 h-4 w-4" data-id="element-4981" data-mp="322cf3jg60moh" /> PDF
          </Button>
          <Button className="bg-teal-600 hover:bg-teal-700" onClick={exportCsv} data-id="element-4982" data-mp="1boljw4kxqe03">
            <FileSpreadsheetIcon className="mr-2 h-4 w-4" data-id="element-4983" data-mp="17n9spumexkps" /> Excel-compatible
            CSV
          </Button>
        </div>
      </div>
      <section className="grid grid-cols-1 gap-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm md:grid-cols-2 xl:grid-cols-4 print:hidden" data-id="element-4984" data-mp="36stgnnx4lbfh">
        <Select label="Report" value={reportType} onChange={event => setReportType(event.target.value as ReportType)} options={reports} data-id="element-4985" data-mp="3bsxwrcu4g456" />
        <Input label="From date" type="date" value={from} onChange={event => setFrom(event.target.value)} data-id="element-4986" data-mp="3lqlpjx8631kp" />
        <Input label="To date" type="date" value={to} onChange={event => setTo(event.target.value)} data-id="element-4987" data-mp="3up7a1xx78qo4" />
        <Select label="Supplier" value={supplierId} onChange={event => setSupplierId(event.target.value)} options={[{
        value: '',
        label: 'All suppliers'
      }, ...suppliers.map(supplier => ({
        value: supplier.id,
        label: supplier.name
      }))]} data-id="element-4988" data-mp="3dnm3gc61qls" />
        <Select label="Category" value={category} onChange={event => setCategory(event.target.value)} options={[{
        value: '',
        label: 'All categories'
      }, ...categories.map(value => ({
        value,
        label: value
      }))]} data-id="element-4989" data-mp="21qs96z7xbl3v" />
        <Select label="Medicine" value={medicineId} onChange={event => setMedicineId(event.target.value)} options={[{
        value: '',
        label: 'All medicines'
      }, ...medicines.map(medicine => ({
        value: medicine.id,
        label: medicine.name
      }))]} data-id="element-4990" data-mp="2lj6wlkkcfjxe" />
        <Select label="Pharmacist" value={pharmacistId} onChange={event => setPharmacistId(event.target.value)} options={[{
        value: '',
        label: 'All pharmacists'
      }, ...users.filter(user => user.role === 'Pharmacy' || user.role === 'Pharmacy Manager').map(user => ({
        value: user.id,
        label: user.fullName
      }))]} data-id="element-4991" data-mp="349ojh8ihvlau" />
      </section>
      <section id="pharmacy-report" className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-4992" data-mp="2qkl8lolj5qyh">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4" data-id="element-4993" data-mp="2hjdlm2ogscjp">
          <div data-id="element-4994" data-mp="2mhyefh1dzty4">
            <h3 className="font-bold text-slate-900" data-id="element-4995" data-mp="2mhf5gptpsfw8">{reportLabel}</h3>
            <p className="mt-1 text-sm text-slate-500" data-id="element-4996" data-mp="163yh8dtxeiek">
              {rows.length} matching records · refreshed from live inventory
            </p>
          </div>
          <p className="text-sm font-semibold text-teal-800" data-id="element-4997" data-mp="1b4cqu0b6e0ep">{summary(rows)}</p>
        </div>
        <div className="overflow-x-auto" data-id="element-4998" data-mp="12kg619da0242">
          <table className="min-w-full divide-y divide-slate-100" data-id="element-4999" data-mp="ykfovgyslctb">
            <thead className="bg-slate-50" data-id="element-5000" data-mp="yk5wxnu6svkz">
              <tr data-id="element-5001" data-mp="1ckqi2qnkr3hn">
                {columns.map(column => <th key={column} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-5002" data-mp="1chulb5ngqkus">
                    {column}
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-5003" data-mp="eudhv4chfgaq">
              {rows.length ? rows.map((row, index) => <tr key={`${index}-${Object.values(row).join('-')}`} data-id="element-5004" data-mp="ou49my9nrh74" data-mpi={index}>
                    {columns.map(column => <td key={column} className="whitespace-nowrap px-5 py-3.5 text-sm text-slate-700" data-id="element-5005" data-mp="1409x1zlc1le">
                        {row[column] ?? '—'}
                      </td>)}
                  </tr>) : <tr data-id="element-5006" data-mp="2p481qqwv1k8w">
                  <td colSpan={columns.length} className="px-5 py-14 text-center text-sm text-slate-500" data-id="element-5007" data-mp="2k4ddkv4gz8rj">
                    No records match these report filters.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>;
}
function buildRows(input: {
  reportType: ReportType;
  medicines: ReturnType<typeof useData>['medicines'];
  medicineBatches: ReturnType<typeof useData>['medicineBatches'];
  purchaseOrders: ReturnType<typeof useData>['purchaseOrders'];
  stockMovements: ReturnType<typeof useData>['stockMovements'];
  supplierId: string;
  category: string;
  medicineId: string;
  pharmacistId: string;
  from: string;
  to: string;
}): ReportRow[] {
  const {
    reportType,
    medicines,
    medicineBatches,
    purchaseOrders,
    stockMovements,
    supplierId,
    category,
    medicineId,
    pharmacistId,
    from,
    to
  } = input;
  const medicineOk = (id: string) => {
    const medicine = medicines.find(item => item.id === id);
    return !!medicine && (!medicineId || id === medicineId) && (!category || medicine.category === category);
  };
  const dateOk = (date: string) => {
    const day = date.slice(0, 10);
    if (reportType === 'daily-dispensing') return isSameDay(date, from);
    if (reportType === 'monthly-dispensing' && from && !to) return date.slice(0, 7) === from.slice(0, 7);
    return (!from || day >= from) && (!to || day <= to);
  };
  const movementRows = stockMovements.filter(movement => medicineOk(movement.medicineId) && dateOk(movement.createdAt) && (!pharmacistId || movement.actorId === pharmacistId));
  const inventory = medicines.filter(medicine => medicineOk(medicine.id));
  const inventoryRows = inventory.map(medicine => ({
    'Drug Code': medicine.drugCode,
    Medicine: medicine.name,
    Category: medicine.category,
    Quantity: medicine.currentQuantity,
    'Reorder Level': medicine.reorderLevel,
    Unit: medicine.unit,
    Value: formatCurrency(medicine.currentQuantity * medicine.costPrice)
  }));
  if (reportType === 'current-inventory' || reportType === 'inventory-valuation') return inventoryRows;
  if (reportType === 'low-stock') return inventory.filter(isLowStockOnly).map(medicine => ({
    Medicine: medicine.name,
    Category: medicine.category,
    Quantity: medicine.currentQuantity,
    'Reorder Level': medicine.reorderLevel,
    Shortfall: Math.max(0, medicine.reorderLevel - medicine.currentQuantity)
  }));
  if (reportType === 'out-of-stock') return inventory.filter(isOutOfStock).map(medicine => ({
    Medicine: medicine.name,
    Category: medicine.category,
    Quantity: medicine.currentQuantity,
    'Reorder Level': medicine.reorderLevel
  }));
  if (reportType === 'near-expiry' || reportType === 'expired' || reportType === 'batch-tracking') return medicineBatches.filter(batch => medicineOk(batch.medicineId) && (!supplierId || batch.supplierId === supplierId) && (reportType === 'batch-tracking' || reportType === 'near-expiry' ? expiryAlertWindow(batch) && expiryAlertWindow(batch) !== 'Expired' : expiryAlertWindow(batch) === 'Expired')).map(batch => ({
    Medicine: medicines.find(medicine => medicine.id === batch.medicineId)?.name || 'Unknown',
    Batch: batch.batchNumber,
    Supplier: batch.supplierName || '—',
    Quantity: batch.quantityRemaining,
    'Expiry Date': batch.expiryDate,
    Alert: expiryAlertWindow(batch) || 'None',
    'Unit Cost': formatCurrency(batch.buyingPrice)
  }));
  if (reportType === 'purchase-orders' || reportType === 'supplier-purchases') return purchaseOrders.filter(order => dateOk(order.dateReceived || order.createdAt) && (!supplierId || order.supplierId === supplierId)).map(order => ({
    'Order / Receipt': order.orderNumber,
    Supplier: order.supplierName,
    Type: order.kind === 'Receipt' ? 'Receipt' : 'Purchase Order',
    Status: order.status,
    Date: (order.dateReceived || order.createdAt).slice(0, 10),
    Cost: formatCurrency(order.totalCost ?? order.lines.reduce((sum, line) => sum + line.quantityOrdered * line.unitCost, 0)),
    'Recorded By': order.receivedByName || order.createdBy
  }));
  const dispensingRows = movementRows.filter(movement => movement.type === 'Dispense');
  const selected = movementRows.filter(movement => reportType === 'daily-dispensing' ? movement.type === 'Dispense' && isSameDay(movement.createdAt, from) : reportType === 'monthly-dispensing' ? movement.type === 'Dispense' && isSameMonth(movement.createdAt, from) : reportType === 'adjustment-history' ? ['Adjustment In', 'Adjustment Out', 'Damaged', 'Transfer', 'Expired Disposal'].includes(movement.type) : reportType === 'medicine-returns' ? ['Return In', 'Return Out'].includes(movement.type) : true);
  if (reportType === 'fast-moving' || reportType === 'slow-moving') {
    const counts = dispensingRows.reduce<Record<string, number>>((next, movement) => ({
      ...next,
      [movement.medicineId]: (next[movement.medicineId] || 0) + Math.abs(movement.quantityDelta)
    }), {});
    return Object.entries(counts).map(([id, quantity]) => ({
      Medicine: medicines.find(medicine => medicine.id === id)?.name || 'Unknown',
      'Units Dispensed': quantity
    })).sort((a, b) => reportType === 'fast-moving' ? Number(b['Units Dispensed']) - Number(a['Units Dispensed']) : Number(a['Units Dispensed']) - Number(b['Units Dispensed']));
  }
  return selected.map(movement => ({
    'Date & Time': new Date(movement.createdAt).toLocaleString(),
    Action: movement.actionPerformed || movement.type,
    Medicine: movement.medicineName,
    Batch: movement.allocations?.map(allocation => allocation.batchNumber).join(', ') || movement.batchNumber || '—',
    Change: movement.quantityDelta,
    'Previous → New': `${movement.quantityBefore} → ${movement.quantityAfter}`,
    Pharmacist: movement.actorName,
    'Authorised Manager': movement.authorisedUserName || '—',
    Reason: movement.reason || '—',
    Comment: movement.notes || '—',
    Reference: movement.prescriptionId || movement.referenceId || '—'
  }));
}
function isSameDay(date: string, selectedDate: string) {
  return !selectedDate || date.slice(0, 10) === selectedDate;
}
function isSameMonth(date: string, selectedDate: string) {
  const month = selectedDate ? selectedDate.slice(0, 7) : new Date().toISOString().slice(0, 7);
  return date.slice(0, 7) === month;
}
function csvValue(value: unknown) {
  return `"${String(value ?? '').replace(/"/g, '""')}"`;
}
function dateStamp() {
  return new Date().toISOString().slice(0, 10);
}
function slug(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}
function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
function summary(rows: ReportRow[]) {
  const numeric = rows.flatMap(row => Object.values(row).filter((value): value is number => typeof value === 'number'));
  return numeric.length ? `Total units: ${numeric.reduce((sum, value) => sum + value, 0)}` : `${rows.length} records`;
}